import React, { useState, useMemo } from "react";
import { Box, Grid, IconButton, Button, CircularProgress } from "@mui/material";
import { useTheme } from "../../theme/ThemeContext";
import { Container } from "../common/Container";
import { AppTypography } from "../common/Typography";
import AppButton from "../common/Button";
import AddressModal from "../common/AddressModal";
import { useAuth } from "../../context/AuthContext";
import { useStoreInfo } from "../../context/StoreInfoContext";
import {
  useCreateAddressMutation,
  useGetAddressesQuery,
  useUpdateAddressMutation,
  useDeleteAddressMutation,
} from "../../store/apiSlice";
import Toaster from "../utils/Toaster";
import {
  HomeOutlined,
  BusinessOutlined,
  EditOutlined,
  DeleteOutlined,
  Add,
  LocationOn,
} from "@mui/icons-material";

const mapApiAddressToCard = (addr) => ({
  id: addr.id,
  type: addr.address_type || "home",
  label: addr.address_type === "work" ? "Work Address" : "Home Address",
  first_name: addr.first_name ?? "",
  last_name: addr.last_name ?? "",
  name: `${addr.first_name || ""} ${addr.last_name || ""}`.trim(),
  phone: addr.phone_number ?? "",
  address1: addr.address_line_1 ?? "",
  address2: addr.address_line_2 ?? "",
  address3: addr.address_line_3 ?? "",
  landmark: addr.landmark ?? "",
  city: addr.city ?? "",
  state: addr.state ?? "",
  pincode: addr.zipcode ?? "",
  country: addr.country ?? "",
  email_id: addr.email_id,
  isDefault: addr.is_default ?? false,
});

const AddressCard = ({ address, onEdit, onDelete, onSetDefault, theme }) => {
  const isHome = address.type === "home";
  const iconBg = isHome
    ? "rgba(33, 150, 243, 0.12)"
    : "rgba(156, 39, 176, 0.12)";
  const iconColor = isHome ? "#2196F3" : "#9C27B0";

  return (
    <Box
      sx={{
        backgroundColor: "#FFFFFF",
        borderRadius: "12px",
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08)",
        border: address.isDefault
          ? "2px solid transparent"
          : "1px solid #E5E7EB",
        backgroundImage: address.isDefault
          ? "linear-gradient(#FFFFFF, #FFFFFF), linear-gradient(135deg, #9810FA 0%, #4F39F6 100%)"
          : "none",
        backgroundOrigin: address.isDefault ? "border-box" : "unset",
        backgroundClip: address.isDefault ? "border-box, border-box" : "unset",
        p: 2,
        position: "relative",
        minHeight: "240px",
        height: "100%",
        width: "100%",
        transition: "box-shadow 0.2s, border-color 0.2s",
        "&:hover": {
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.12)",
        },
      }}
    >
      {/* Top row: Icon + Heading + Set Default - flexWrap prevents overlap at 600px */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1,
          mb: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            minWidth: 0,
            flex: "1 1 auto",
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              flexShrink: 0,
              borderRadius: "10px",
              backgroundColor: iconBg,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mr: 1.5,
            }}
          >
            {isHome ? (
              <HomeOutlined sx={{ fontSize: 22, color: iconColor }} />
            ) : (
              <BusinessOutlined sx={{ fontSize: 22, color: iconColor }} />
            )}
          </Box>
          <AppTypography
            variant="subtitle1"
            sx={{ fontWeight: 400, fontSize: "16px", color: "#101828" }}
          >
            {address.label}
          </AppTypography>
        </Box>
        <Box sx={{ flexShrink: 0 }}>
          {address.isDefault ? (
            <Box
              sx={{
                px: 1.5,
                py: 0.5,
                borderRadius: "20px",
                background: "linear-gradient(135deg, #9810FA 0%, #4F39F6 100%)",
                color: "#FFFFFF",
                fontSize: "12px",
                fontWeight: 400,
              }}
            >
              Default
            </Box>
          ) : (
            <Button
              variant="outlined"
              size="small"
              onClick={() => onSetDefault(address.id)}
              sx={{
                color: "#364153",
                border: "1px solid rgb(182, 178, 178)",
                fontSize: "14px",
                fontWeight: 400,
                textTransform: "none",
                px: 1.5,
                py: 0.5,
                minHeight: "28px",
                borderRadius: "14px",
                "&:hover": {
                  backgroundColor: "#E5E7EB",
                },
              }}
            >
              Set Default
            </Button>
          )}
        </Box>
      </Box>

      {/* Address details - aligned below the heading (icon + gap = 52px) */}
      <Box sx={{ pl: "52px", mt: 1.5 }}>
        <AppTypography
          variant="body2"
          sx={{
            fontSize: "14px",
            fontWeight: 400,
            color: "#4A5565",
            mb: 0.25,
            wordBreak: "break-word",
            overflowWrap: "anywhere",
            lineHeight: 1.35,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {`${address.first_name || ""} ${address.last_name || ""}`.trim()}
        </AppTypography>

        <AppTypography
          variant="body2"
          sx={{
            fontSize: "14px",
            fontWeight: 400,
            color: "#4A5565",
            mb: 0.75,
            wordBreak: "break-word",
            overflowWrap: "anywhere",
          }}
        >
          {address.phone}
        </AppTypography>

        <AppTypography
          variant="body2"
          sx={{
            fontSize: "14px",
            fontWeight: 400,
            color: "#4A5565",
            mb: 0.25,
            wordBreak: "break-word",
            overflowWrap: "anywhere",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {address.address1}
        </AppTypography>
        {address.address2 ? (
          <AppTypography
            variant="body2"
            sx={{
              fontSize: "14px",
              fontWeight: 400,
              color: "#4A5565",
              mb: 0.25,
              wordBreak: "break-word",
              overflowWrap: "anywhere",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {address.address2}
          </AppTypography>
        ) : null}

        <AppTypography
          variant="body2"
          sx={{
            fontSize: "14px",
            fontWeight: 400,
            color: "#4A5565",
            mb: 1,
            wordBreak: "break-word",
            overflowWrap: "anywhere",
          }}
        >
          {address.city}, {address.state} {address.pincode}
        </AppTypography>

        {/* Edit & Delete - below address, same left alignment */}
        <Box sx={{ display: "flex", gap: 0.5, mt: 0.5 }}>
          <IconButton
            size="small"
            onClick={() => onEdit(address)}
            sx={{
              color: "#6B7280",
              "&:hover": { backgroundColor: "rgba(0,0,0,0.04)" },
            }}
          >
            <EditOutlined sx={{ fontSize: 18 }} />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => onDelete(address.id)}
            sx={{
              color: "#DC2626",
              "&:hover": { backgroundColor: "rgba(220, 38, 38, 0.08)" },
            }}
          >
            <DeleteOutlined sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

const MyAddresses = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const { customerId, storeId } = useStoreInfo();
  const [createAddress, { isLoading: isCreating }] = useCreateAddressMutation();
  const [updateAddress, { isLoading: isUpdating }] = useUpdateAddressMutation();
  const [deleteAddress, { isLoading: isDeleting }] = useDeleteAddressMutation();
  const {
    data: addressesData,
    isLoading: isLoadingAddresses,
    isError: isAddressesError,
    error: addressesError,
    refetch: refetchAddresses,
  } = useGetAddressesQuery(
    { storeId, customerId, userId: user?.id },
    { skip: !customerId || !user?.id },
  );
  const [modalOpen, setModalOpen] = useState(false);
  const [editAddress, setEditAddress] = useState(null);

  const rawList = addressesData?.result ?? addressesData?.data ?? addressesData;
  const addresses = useMemo(
    () => (Array.isArray(rawList) ? rawList.map(mapApiAddressToCard) : []),
    [rawList],
  );

  const handleSetDefault = async (id) => {
    if (!user?.id || !customerId) return;
    try {
      await updateAddress({
        storeId,
        addressId: id,
        customerId,
        userId: user.id,
        is_default: true,
      }).unwrap();
      await refetchAddresses();
      Toaster.show("Default address updated", "success");
    } catch (err) {
      const msg =
        err?.data?.message || err?.message || "Failed to set default address";
      Toaster.show(msg, "error");
    }
  };

  const handleEdit = (address) => {
    setEditAddress(address);
    setModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!customerId) return;
    try {
      await deleteAddress({
        storeId,
        userId: user.id,
        addressId: id,
        customerId,
      }).unwrap();
      await refetchAddresses();
      Toaster.show("Address deleted successfully", "success");
    } catch (err) {
      const msg =
        err?.data?.message || err?.message || "Failed to delete address";
      Toaster.show(msg, "error");
    }
  };

  const handleAddNew = () => {
    if (!user?.id) {
      Toaster.show("Please log in to add an address", "warning");
      return;
    }
    setEditAddress(null);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setEditAddress(null);
  };

  const handleAddressSubmit = async (formData) => {
    if (!user?.id) {
      Toaster.show("Please log in to add an address", "warning");
      throw new Error("Not authenticated");
    }

    const apiPayload = {
      first_name: formData.firstName || "",
      last_name: formData.lastName || "",
      email_id: formData.email,
      phone_number: formData.phone,
      address_line_1: formData.address1,
      address_line_2: formData.address2 || undefined,
      address_line_3: formData.address3 || undefined,
      landmark: formData.landmark || undefined,
      city: formData.city,
      state: formData.state,
      country: formData.country,
      zipcode: formData.pincode,
      address_type: formData.type,
      is_default: formData.isDefault,
    };

    try {
      if (editAddress) {
        await updateAddress({
          storeId,
          addressId: editAddress.id,
          customerId,
          userId: user.id,
          ...apiPayload,
        }).unwrap();
        await refetchAddresses();
        Toaster.show("Address updated successfully", "success");
      } else {
        await createAddress({
          userId: user.id,
          storeId,
          customerId,
          ...apiPayload,
        }).unwrap();
        await refetchAddresses();
        Toaster.show("Address added successfully", "success");
      }
    } catch (err) {
      const msg =
        err?.data?.message ||
        err?.message ||
        (editAddress ? "Failed to update address" : "Failed to add address");
      Toaster.show(msg, "error");
      throw err;
    }
  };

  return (
    <Container maxWidth="md">
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          mb: 3,
          gap: 2,
        }}
      >
        <Box>
          <AppTypography
            variant="h2"
            sx={{
              color: "#101828",
              fontWeight: 400,
              fontSize: "24px",
            }}
          >
            My Addresses
          </AppTypography>
          <AppTypography
            variant="body1"
            component="p"
            sx={{
              color: "#4A5565",
              fontWeight: 400,
              fontSize: "16px",
              mt: 0.5,
            }}
          >
            Manage your delivery addresses
          </AppTypography>
        </Box>
        <AppButton
          variant="contained"
          startIcon={<Add />}
          onClick={handleAddNew}
          sx={{
            background: theme?.app_button_color,
            color: theme?.app_button_text_color || "#fff",
            borderRadius: "14px",
            textTransform: "none",
            fontWeight: 400,
            fontSize: "14px",
            px: 1.5,
            py: 1,
            "&:hover": {
              background: theme?.app_hover_button_color,
            },
          }}
        >
          Add New
        </AppButton>
      </Box>

      {/* Address cards grid */}
      {isLoadingAddresses ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : isAddressesError ? (
        <Box sx={{ py: 4 }}>
          <AppTypography sx={{ color: "#DC2626", textAlign: "center" }}>
            {addressesError?.data?.message ||
              addressesError?.message ||
              "Failed to load addresses"}
          </AppTypography>
        </Box>
      ) : addresses.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 6,
            px: 2,
          }}
        >
          <Box
            sx={{
              width: 60,
              height: 60,
              borderRadius: "50%",
              background: "linear-gradient(135deg, #9810FA 0%, #4F39F6 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 3,
            }}
          >
            <LocationOn sx={{ fontSize: 30, color: "#fff" }} />
          </Box>
          <AppTypography
            variant="h2"
            sx={{
              color: "#101828",
              fontWeight: 600,
              fontSize: "20px",
              mb: 1,
              textAlign: "center",
            }}
          >
            No Addresses Added Yet
          </AppTypography>
          <AppTypography
            sx={{
              color: "#4A5565",
              fontSize: "14px",
              textAlign: "center",
              mb: 3,
              maxWidth: 320,
            }}
          >
            Add your home or work address to continue with delivery.
          </AppTypography>
          <AppButton
            variant="contained"
            startIcon={<Add />}
            onClick={handleAddNew}
            sx={{
              background: "linear-gradient(135deg, #9810FA 0%, #4F39F6 100%)",
              color: "#fff",
              borderRadius: "14px",
              textTransform: "none",
              fontWeight: 500,
              fontSize: "14px",
              px: 3,
              py: 1.5,
              "&:hover": {
                background: "linear-gradient(135deg, #8B0EE5 0%, #4530DB 100%)",
              },
            }}
          >
            Add New Address
          </AppButton>
          <AppTypography
            sx={{
              color: "#6B7280",
              fontSize: "12px",
              textAlign: "center",
              mt: 2,
            }}
          >
            You can add multiple delivery addresses anytime
          </AppTypography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {addresses.map((address) => (
            <Grid item xs={12} lg={6} key={address.id} sx={{ display: "flex" }}>
              <AddressCard
                address={address}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onSetDefault={handleSetDefault}
                theme={theme}
              />
            </Grid>
          ))}
        </Grid>
      )}

      <AddressModal
        open={modalOpen}
        onClose={handleModalClose}
        onSubmit={handleAddressSubmit}
        editAddress={editAddress}
        userEmail={user?.email}
      />
    </Container>
  );
};

export default MyAddresses;
